import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App } from 'antd';

import useCollectionItems from './useCollectionItems';
import { canonicalize } from '../utils/kqlBuilder';
import { replaceTagName } from '../utils/filterValues';
import { TOO_LONG_CODE, elementRefusalOf, elementRefusalText, refusalSentence, withElementChoice } from '../utils/elementRefusal';
import { isSearchRefusal } from '../utils/searchRefusal';

const COMPOSED_TEXT = 'The server would not run this list’s filter.';
const COMPOSED_TOAST = 'This list’s filter could not be read.';

/**
 * A list's filter, and the items feed it asks: everything between the filter
 * text a person typed and the rows that come back. The collection list and a
 * related list share it, so a filter behaves the same on both.
 *
 *   const list = useListFilter({
 *     query, setQuery,                       // the person's own filter text
 *     collectionId, cardFilter, sortField, sortDirection, …   // the feed's parameters
 *   });
 *   list.items, list.totalCount, list.loading, …   // the feed (useCollectionItems)
 *   list.queryRefusal, list.pickForRefusal, …      // what the filter was refused for
 *
 * The caller owns WHERE the text lives (the collection list keeps it in the
 * address, a related list in its own state) and hands over the text and its
 * writer. What this holds:
 *
 * - **Refusals.** A filter the server will not run is never left to fail on
 *   every render. A plain 400 (a stale link, an API without the query engine)
 *   drops the filter with a toast. An element name several elements answer to
 *   (`ambiguous_element`), a tag name several tags share (`ambiguous_tag`) and
 *   a filter that grew past the engine's length cap with the person's picks
 *   (`query_too_long`) keep the text: `queryRefusal` says what happened,
 *   `pickForRefusal(value)` answers it, `repick` (when there are picks to
 *   forget) asks again. The owner's ruling: a filter names the element itself,
 *   and the person says which.
 * - **A composed feed.** When the caller ran something other than the person's
 *   text (`feedQuery`: the collection list ANDs a card's worklist filter in),
 *   a refusal is not the person's to lose: clearing the filter they can see in
 *   the chips would not fix it. It lands in `composedError` instead (the
 *   caller decides what to draw - the worklist refuses to draw over the
 *   unfiltered rows it was meant to exclude), `clearComposedError()` ends it,
 *   and it ends by itself when the feed, the collection or the card changes.
 * - **A refused search term** (KPR-740). When the server refuses the search
 *   box's `text ~` term (`isSearchRefusal`: too many words, too broad ...),
 *   `onSearchRefused(body, message)` answers first, and returning true keeps the
 *   filter: the rest of the person's query is not the thing that failed, so it
 *   is neither dropped nor toasted (`useListSearch` owns what happens next).
 * - **Elements picks** (`elementChoices`) belong to the feed they answer: sent
 *   with that feed's `q`, forgotten with the old text.
 * - **Canonical adoption** (deterministic filters F3). What the server read the
 *   text as (`X-Query-Canonical`: names resolved to ids, a choice label to its
 *   value) becomes the text, once, so the chips, the builder, saved views and
 *   recent filters all read one spelling. Only the person's OWN text - a
 *   composed feed is not theirs to rewrite - and guarded against a cycle
 *   (canonical of the canonical naming the typed text again); a text the
 *   person sets again starts it over. `adoptions` maps each typed text to the
 *   canonical one it became, so a view saved before still reads as the filter
 *   on screen; `onAdopted(typed, canonical)` lets the caller record it.
 * - **Drift.** `removedIds`: the ids the feed's `X-Query-Warnings` say name
 *   something removed - a deleted card, a set no card carries - so the chips
 *   and the Advanced tab can say so. `queryWarnings` itself is the feed's.
 *
 * Everything the feed takes (`collectionId`, `cardFilter`, `includeLinked`,
 * `review`, `sortField`, `sortDirection`, `sortThen`, `enabled`, `extraParams`) is passed
 * through; the feed's answer comes back as it is.
 */
export default function useListFilter({
	query,
	setQuery,
	feedQuery = query,
	composedMessages,
	onAdopted,
	onSearchRefused,
	collectionId,
	cardFilter,
	includeLinked,
	review,
	sortField,
	sortDirection,
	sortThen,
	enabled,
	extraParams,
}) {
	const { message } = App.useApp();
	const composedText = composedMessages?.error || COMPOSED_TEXT;
	const composedToast = composedMessages?.toast || COMPOSED_TOAST;

	// Whether what went to the server is the person's text or a composition of
	// it with someone else's. Read only from the async 400 handler, so a ref: it
	// must not put a new onQueryError identity into the feed's deps.
	const composedFeedRef = useRef(false);
	useEffect(() => {
		composedFeedRef.current = feedQuery !== query;
	}, [feedQuery, query]);
	// Whoever owns the search box, read by the same handler (a ref: its identity
	// must not reach the feed's dependencies either).
	const onSearchRefusedRef = useRef(onSearchRefused);
	onSearchRefusedRef.current = onSearchRefused;
	// The feed a refusal answered, read by the same handler.
	const feedQueryRef = useRef(feedQuery);
	feedQueryRef.current = feedQuery;

	// A composed feed the server refused. The feed hook leaves the PREVIOUS
	// page of items on screen when a fetch fails, so a caller drawing a pass
	// over it would draw the unfiltered rows it was meant to exclude. A toast
	// cannot carry that; the list has to refuse to draw.
	const [composedError, setComposedError] = useState(null);
	const clearComposedError = useCallback(() => setComposedError(null), []);

	// A refusal the person can settle by picking - a tag name several tags
	// share (400 `ambiguous_tag`, deterministic filters ruling E2), an element
	// name several elements answer to (400 `ambiguous_element`, element ids
	// step 3) - or must edit (400 `unknown_element`): the filter stays, and
	// the list offers what the name could be, or says what it names nothing.
	const [queryRefusal, setQueryRefusal] = useState(null);

	// The person's answers to `ambiguous_element`, for the feed they answer
	// (`{ feed, choices }`): sent with that feed's `q` as `elementChoices`
	// (utils/elementRefusal.js). The server then writes the ids, the list
	// adopts its canonical text (below), and the picks - tied to the feed -
	// fall away with the old text. A composed feed is never adopted, so its
	// picks stay while the feed does.
	const [elementPicks, setElementPicks] = useState(null);
	const elementChoices = elementPicks && elementPicks.feed === feedQuery ? elementPicks.choices : null;

	// A query the server refuses (a stale link, an API without the query
	// engine) is dropped rather than left to fail on every render.
	const handleQueryError = useCallback((serverMessage, body = {}) => {
		// The search box's own term: the list keeps everything else.
		if (isSearchRefusal(body) && onSearchRefusedRef.current?.(body, serverMessage)) return;
		// An element name the server could not settle is never dropped: a
		// saved filter or a bookmark that names one keeps its text, and the
		// list asks - the person's own query and a composed feed alike.
		// Nothing is drawn meanwhile (the feed cleared its rows).
		const element = elementRefusalOf(body);
		if (element) {
			setQueryRefusal({ ...element, feed: feedQueryRef.current });
			if (composedFeedRef.current) setComposedError(elementRefusalText(element));
			return;
		}
		// Too long - a pick of "any of these cards" repeats its condition per
		// card, and a filter holds at most 50 conditions: the filter stays,
		// with the server's sentence, and the picks can be made again.
		if (body && body.code === TOO_LONG_CODE) {
			const tooLong = { code: TOO_LONG_CODE, message: String(body.message || serverMessage || ''), field: String(body.field || ''), feed: feedQueryRef.current };
			setQueryRefusal(tooLong);
			if (composedFeedRef.current) setComposedError(refusalSentence(tooLong));
			return;
		}
		// Only the person's OWN query is theirs to lose. When someone else's
		// filter is composed in, a refusal is that filter's fault, and clearing
		// the one the person can see in the chips over it would be exactly the
		// damage composing it ahead of time is meant to prevent.
		if (composedFeedRef.current) {
			setComposedError(serverMessage || composedText);
			message.error(serverMessage || composedToast);
			return;
		}
		if (body && body.code === 'ambiguous_tag' && Array.isArray(body.candidates) && body.candidates.length) {
			setQueryRefusal({ code: body.code, value: body.value, candidates: body.candidates });
			return;
		}
		message.error(serverMessage || 'That filter could not be read, so it was removed.');
		setQuery(undefined);
	}, [message, setQuery, composedText, composedToast]);
	// The feed is handed ONE function for its lifetime, which calls whatever
	// the handler is now: a new identity here must never reach the feed's
	// dependencies and restart it.
	const queryErrorRef = useRef(handleQueryError);
	queryErrorRef.current = handleQueryError;
	const onFeedQueryError = useCallback((serverMessage, body) => queryErrorRef.current(serverMessage, body), []);

	const feed = useCollectionItems({
		collectionId,
		cardFilter,
		includeLinked,
		review,
		query: feedQuery,
		elementChoices,
		sortField,
		sortDirection,
		sortThen,
		onQueryError: onFeedQueryError,
		enabled,
		extraParams,
	});
	const { canonicalQuery, queryWarnings } = feed;

	// What the server read the person's query as (`X-Query-Canonical`) becomes
	// the query, once. The guard is against a cycle (canonical of the canonical
	// naming the typed text again), so a query the PERSON sets - a saved view
	// applied a second time - starts it over (the F3 review's F4).
	const adoptedRef = useRef(new Set());
	const adoptingRef = useRef(null);
	useEffect(() => {
		if (query !== adoptingRef.current) adoptedRef.current = new Set();
	}, [query]);
	// What each typed text was adopted as, so a saved view stored before F2
	// still reads as the filter on screen (SavedFilterPicker `adoptions`).
	const [adoptions, setAdoptions] = useState(() => new Map());
	const onAdoptedRef = useRef(onAdopted);
	onAdoptedRef.current = onAdopted;
	useEffect(() => {
		if (!canonicalQuery || !query || feedQuery !== query) return;
		const next = canonicalize(canonicalQuery);
		const typed = canonicalize(query);
		if (!next || next === typed) return;
		const step = `${query}\u0000${next}`;
		if (adoptedRef.current.has(step)) return;
		adoptedRef.current.add(step);
		adoptingRef.current = next;
		setAdoptions(prev => (prev.get(typed) === next ? prev : new Map(prev).set(typed, next)));
		onAdoptedRef.current?.(query, next);
		setQuery(next);
	}, [canonicalQuery, query, feedQuery, setQuery]);

	// A refusal belongs to the query it refused - the feed it refused, where a
	// composition is in play.
	useEffect(() => { setQueryRefusal(null); }, [query, feedQuery]);

	// The person's answer: a tag's id written into the text; an element's
	// id, or every id ("Any of these cards"), sent with the same feed as
	// `elementChoices` - the server writes it, and the list adopts that text.
	const pickForRefusal = useCallback((value) => {
		const refusal = queryRefusal;
		if (!refusal) return;
		if (refusal.code === 'ambiguous_tag') {
			setQuery(replaceTagName(query, refusal.value, value));
			return;
		}
		setElementPicks(prev => ({
			feed: refusal.feed,
			choices: withElementChoice(prev && prev.feed === refusal.feed ? prev.choices : {}, refusal, value),
		}));
		setQueryRefusal(null);
		setComposedError(null);
	}, [queryRefusal, query, setQuery]);

	// Too long after a pick: forget this feed's picks, and the list asks again.
	const repick = useMemo(() => (elementChoices
		? () => { setElementPicks(null); setQueryRefusal(null); setComposedError(null); }
		: null), [elementChoices]);

	// The ids the server said name something removed.
	const removedIds = useMemo(() => new Set((queryWarnings || []).flatMap(warning => (warning.names || [])
		.filter(entry => entry.removed)
		.map(entry => String(entry.name).toLowerCase()))), [queryWarnings]);

	// A new question clears the old refusal: the error belongs to one feed, not
	// to the page. (The refusal arrives after this effect has run for the feed
	// it refuses, so it can never be cleared by its own fetch.)
	useEffect(() => {
		setComposedError(null);
	}, [feedQuery, collectionId, cardFilter]);

	return {
		...feed,
		elementChoices,
		queryRefusal,
		pickForRefusal,
		repick,
		composedError,
		clearComposedError,
		adoptions,
		removedIds,
	};
}
